/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Container, Grid, RichText, Section, Heading,
} from '@printdeal/ui-library';
import { Experiment, useExperiment, useFeatureConfig } from '@printdeal/feature-management/react';
import { ErrorManagement } from '@printdeal/error-management/react';
import { Markdown } from '@printdeal/markdown/react';
import cx from 'clsx';
import { InstantSearch } from 'react-instantsearch';
import { useLocation } from '../../hooks/useSafeLocation';
import { useReadyForCypress } from '../../hooks/useReadyForCypress';
import { dispatchDataLayerEntry } from '../../helpers/dataLayerHelpers/productDataLayerHelper';
import ModuleFullWidth from '../contentful-rich-text/FullWidth';
import { SupportArticleCard } from '../help-center/SupportArticleCard';
import LinkedFaqs from '../product/detail/LinkedFaqs';
import ProductsSection from './ProductsSection';
import { InteractiveBanner } from '../interactive-banner/InteractiveBanner';
import { GuidedSellingBanner } from './guided-selling/GuidedSellingBanner';
import { VerticalFeaturedBanner } from './VerticalFeaturedBanner';
import { usePagesExperiment } from '../../hooks/usePagesExperiment';
import { FallbackSpinnerComponent } from '../FallbackSpinnerComponent';
import type { Banner, ContentfulStaticBanner, ContentfulInteractiveBanner } from '../../types/banner';
import type { LinkedSupportArticle } from '../../types/helpCenter/helpCenter';
import type { ProductListingPage } from '../../types/product/productListingPage';
import type { Product } from '../../types/category/category';
import type { IProductGalleryImage } from '../../types/product/productDetailPage';
import type { ListProps } from '../homepage/product-list/types';
import { CategoryCardGallery } from './CategoryCardGallery';
import { FacetFilteringBanner } from './guided-selling/FacetFilteringBanner';
import { INDICES } from '../algolia-search/helper/algoliaHelper';
import algoliaClient from '../algolia-search/algoliaClient';
import { getFilterProducts } from '../algolia-search/helper/algoliaFacets';

const PAGE_NAME = 'product_category_page';

interface Props {
  data: ProductListingPage,
  showShopInShop: boolean,
}

interface Exp1105Data {
  products: {
    list: {
      [locale: string]: ListProps[];
    };
  };
}

interface AlternativeImage {
  __typename: 'ContentfulAlternativeImage';
  id: string;
  alternativeImage?: IProductGalleryImage;
  productDetailPage: Product;
}

type Group = {
  __typename: 'ContentfulProductListingGroup';
  id: string;
  title?: string;
  description?: string;
  backgroundColor?: string;
  products: Item[];
};

type Item = Product | AlternativeImage;
export type GroupOrItem = Group | Item;

export const isGroup = (item: GroupOrItem): item is Group => (
  item.__typename === 'ContentfulProductListingGroup'
);

export const isAlternativeImage = (item: GroupOrItem): item is AlternativeImage => (
  item.__typename === 'ContentfulAlternativeImage'
);

const isInteractiveBanner = (item: Banner): item is ContentfulInteractiveBanner => (
  item.__typename === 'ContentfulInteractiveImage'
);

const isStaticBanner = (item: Banner): item is ContentfulStaticBanner => (
  item.__typename === 'ContentfulModuleFullWidth'
);

/** Currently the ProductListingGroup is only being displayed on Shop-in-Shop pages * */
const convertProducts = (products: GroupOrItem[], shopInShop: boolean, isVerticalBannerExp: boolean) => {
  if (!products) {
    return [];
  }

  if (shopInShop) {
    // if vertical banner experiment is enabled, remove the first product group (4 products) from the list
    return isVerticalBannerExp ? products.slice(1) : products;
  }

  // If it isn't a Shop-in-Shop page, create a single array with all the Product Detail Pages.
  return products.reduce((groups, item) => {
    if (isGroup(item)) {
      return [...groups, ...item.products];
    }

    if (isAlternativeImage(item)) {
      return [...groups, { ...item.productDetailPage, image: item.alternativeImage }];
    }

    return [...groups, item];
  }, [] as GroupOrItem[]);
};

const StaticBanners = ({ staticTopBanners, totalBanners, contentfulId }: {
  staticTopBanners: ContentfulStaticBanner[],
  totalBanners: number,
  contentfulId: string,
}) => (
  <Grid className={cx('pd-grid-cols-1 pd-gap-4', {
    'lg:pd-grid-cols-2': totalBanners > 1,
  })}
  >
    {staticTopBanners.map((banner, index: number) => (
      <Grid.Item key={banner.bannerTitle} className={index > 0 ? 'pd-hidden sm:pd-block' : 'pd-block'}>
        <ModuleFullWidth
          data={{
            target: banner,
            clickHandler: () =>
              dispatchDataLayerEntry('product_category_page', 'click', {
                'element-type': 'Top banner',
                contentful_id: contentfulId,
              }),
          }}
        />
      </Grid.Item>
    ))}
  </Grid>
);

const CategoryPage = ({ data, showShopInShop = false }: Props) => {
  const intl = useIntl();
  const { locale } = intl;
  const { origin } = useLocation();
  const readyForCypressId = useReadyForCypress('product-category-page-loaded');
  const exp1489 = useExperiment('ninjas_exp1489_add_clickable_products_on_pictures');
  const exp1432 = useExperiment('ninjas_exp1432_pdp_quick_view_on_plp');
  const exp1868 = useExperiment('base_boosters_exp1868_use_products_from_algolia_to_make_plp_dynamic');
  const loadingMessage = intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' });
  const client = algoliaClient();

  const {
    productCategory, productCategoryCardImage,
  } = data;
  const productCategoryPage = productCategory;
  const {
    title,
    slug: categorySlug,
    categories,
    productsTitle,
    introduction,
    products,
    categoryName,
    shopInShopContent,
    contentful_id: contentfulId,
    content,
    secondaryTitle,
  } = productCategoryPage;

  const [algoliaProducts, setAlgoliaProducts] = useState<Product[]>([]);

  const { experiment: exp1105, status: statusExp1105, data: exp1105Data } = usePagesExperiment<Exp1105Data>('exp1105_vertical_banner_product_listing_page', 'exp1105_vertical_banner_product_listing_pages', contentfulId);
  const isInitializing = exp1489.isInitializing && statusExp1105 === 'determining';
  const isInitialized = exp1489.isInitialized && statusExp1105 === 'determined';
  const noVariantEnabled = exp1489.experiment.variation !== 'variant' && exp1105?.variation !== 'variant';
  const isExp1868Variant = exp1868.isInitialized && exp1868.experiment.variation === 'variant';

  const topBanners = shopInShopContent?.topBanner;
  const interactiveBanner = topBanners?.find(isInteractiveBanner);
  const staticTopBanners = topBanners?.filter(isStaticBanner);
  const totalStaticBanners = staticTopBanners?.length || 0;
  const isVerticalBannerExp = exp1105?.variation === 'variant';
  const isQuickViewExp = exp1432.experiment.variation === 'variant';
  const linkedFaqs = shopInShopContent?.linkedFaqs;
  const linkedSupportArticles = shopInShopContent?.linkedSupportArticles;
  const productList = useMemo(() =>
    convertProducts(products, showShopInShop, isVerticalBannerExp), [products, showShopInShop, isVerticalBannerExp]);
  const { featureConfig: exp1859config } = useFeatureConfig<{ contentfulIds: string[] }>('base_boosters_exp1859_show_ai_guided_selling_config');
  const showGuidedSelling = exp1859config?.variables?.contentfulIds?.includes(contentfulId);

  const handleOnClick = () => {
    dispatchDataLayerEntry(PAGE_NAME, 'click_bottom_banner', {
      contentfulid: shopInShopContent?.bottomBanner?.contentful_id,
      title: shopInShopContent?.bottomBanner?.bannerTitle,
    });
  };

  useEffect(() => {
    if (isInitialized && exp1489.experiment.variation === 'variant') {
      dispatchDataLayerEntry(
        'product_listing_page', 'view_interactive_banner', {
          experiment: exp1489.experiment.id,
        },
      );
    }
  }, [isInitialized, exp1489.experiment]);

  useEffect(() => {
    const fetchProducts = async () => {
      setAlgoliaProducts(await getFilterProducts(client, INDICES.PDP_INDEX, categorySlug));
    };
    fetchProducts().catch((e) => {
      ErrorManagement.exception({
        exception: e,
        tags: {
          component: 'CategoryPage',
          fileLocation: 'components-product-category-CategoryPage',
        },
        fingerprint: ['CategoryPage-FacetFiltering-products'],
      });
    });
  }, [client, categorySlug]);

  return (
    <div data-cy={readyForCypressId}>
      <Section>
        <Container className="pd-grid pd-gap-8">
          <div>
            <Heading className="pd-mb-2">{title}</Heading>
            {introduction && (
              <RichText as={Markdown} origin={origin} className="pd-max-w-none">{introduction.introduction}</RichText>
            )}
          </div>

          {isInitializing && (<FallbackSpinnerComponent loadingMessage={loadingMessage} />)}

          {isInitialized && showShopInShop && interactiveBanner && totalStaticBanners > 0 && exp1489.experiment.variation === 'variant' && (
            <InteractiveBanner
              image={interactiveBanner.image}
              imageMobile={interactiveBanner.imageMobile}
            />
          )}

          {isInitialized && isVerticalBannerExp && (
            <VerticalFeaturedBanner
              products={exp1105Data?.products?.list?.[locale]}
            />
          )}

          {isInitialized && staticTopBanners && showShopInShop && totalStaticBanners > 0 && noVariantEnabled && (
            <StaticBanners
              staticTopBanners={staticTopBanners}
              totalBanners={totalStaticBanners}
              contentfulId={contentfulId}
            />
          )}

          {categories && categories.length > 0 && (
            <CategoryCardGallery
              categories={categories}
              cardImage={productCategoryCardImage}
            />
          )}

          {showGuidedSelling && (
            <Experiment id="base_boosters_exp1859_show_ai_guided_selling" fallback>
              <Experiment.Variant>
                <InstantSearch searchClient={client} indexName={INDICES.PDP_INDEX}>
                  <FacetFilteringBanner category={categorySlug} pageName={PAGE_NAME} client={client} />
                </InstantSearch>
              </Experiment.Variant>
            </Experiment>
          )}

          <Experiment id="ninjas_exp1530_guided_selling_solution">
            <Experiment.Variant>
              <GuidedSellingBanner pageName={PAGE_NAME} title={title} contentfulId={contentfulId} />
            </Experiment.Variant>
          </Experiment>

        </Container>
      </Section>

      {(isExp1868Variant && algoliaProducts.length > 2) ? (
        <ProductsSection
          categoryName={categoryName}
          title={productsTitle}
          products={algoliaProducts}
          isQuickViewExp={isQuickViewExp}
        />
      ) : (
        <ProductsSection
          categoryName={categoryName}
          title={productsTitle}
          products={productList}
          isQuickViewExp={isQuickViewExp}
        />
      )}

      {showShopInShop && shopInShopContent?.bottomBanner && (
        <Container>
          <ModuleFullWidth
            data={{
              target: shopInShopContent.bottomBanner,
              clickHandler: handleOnClick,
            }}
          />
        </Container>
      )}

      {showShopInShop && linkedSupportArticles && (
        <Section>
          <Container>
            <Heading as="h2">
              <FormattedMessage id="helpcenter.article.related.article" defaultMessage="Related articles" />
            </Heading>
            <Grid className="pd-mt-8 pd-grid-cols-1 pd-items-stretch pd-gap-4 md:pd-grid-cols-3">
              {linkedSupportArticles.map((linkedArticle: LinkedSupportArticle) => (
                <Grid.Item key={linkedArticle.title}>
                  <SupportArticleCard linkedArticle={linkedArticle} pageName={PAGE_NAME} />
                </Grid.Item>
              ))}
            </Grid>
          </Container>
        </Section>
      )}

      {(content || (showShopInShop && linkedFaqs)) && (
        <Section variant={showShopInShop ? 'dim-branded' : 'white'}>
          <Container>
            <Grid
              className={cx('pd-grid-cols-1', ' pd-gap-4', {
                'md:pd-grid-cols-2': showShopInShop && linkedFaqs,
                'md:pd-grid-cols-1': !showShopInShop && !linkedFaqs,
              })}
            >
              {content && (
                <Grid.Item>
                  <Heading as="h2" className="pd-mb-6">
                    {secondaryTitle}
                  </Heading>
                  <RichText as={Markdown} origin={origin} className="pd-max-w-none">{content.content}</RichText>
                </Grid.Item>
              )}
              {showShopInShop && linkedFaqs && (
                <Grid.Item>
                  <LinkedFaqs
                    linkedFaqs={linkedFaqs}
                    productName={title || categoryName}
                    contentful_id={contentfulId}
                    pageName="product_category_page"
                  />
                </Grid.Item>
              )}
            </Grid>
          </Container>
        </Section>
      )}

      {showShopInShop && shopInShopContent.contentMarketingText && (
        <Section>
          <Container>
            <RichText as={Markdown} origin={origin} className="pd-max-w-none">{shopInShopContent.contentMarketingText.childMarkdownRemark.rawMarkdownBody}</RichText>
          </Container>
        </Section>
      )}
    </div>
  );
};

export default CategoryPage;
